// Single button that advances through the value-type's chart catalog.
//
// Convention (matches the old chart↔stats toggle): the icon shown is the
// chart you're about to see, not the one you're looking at. Click writes
// a per-feature override and the container re-renders.
//
// The kebab next to the cycler holds the demoted "Set as default for all
// <type> features" / "Reset to default" actions — only shown when the
// user has set a per-feature override, so it doesn't crowd the common
// case (one button, one click, next chart).

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChartId, ValueType } from './types';
import { VALUE_TYPE_CATALOG, systemDefaultFor } from './registry';
import { CHART_ICONS, CHART_LABELS } from './icons';
import type { ChartPrefsApi } from './useChartPrefs';

export interface KebabMenuItem {
  label: string;
  onClick: () => void;
}

interface Props {
  featureId: number;
  valueType: ValueType;
  currentChartId: ChartId;
  prefs: ChartPrefsApi;
  // Extra items to mount in the kebab below the prefs items. The kebab
  // becomes always-visible when this is non-empty, even with no feature
  // pref set, so callers can use it as the canonical chart-options menu.
  extraMenuItems?: KebabMenuItem[];
}

const VALUE_TYPE_LABELS: Record<ValueType, string> = {
  decimal: 'numeric',
  rating: 'rating',
  freeform: 'freeform',
  enum: 'enum',
  bool: 'yes/no',
  reference: 'reference',
  list: 'list',
  map: 'map',
  map_numeric: 'numeric map',
  map_ordered_numeric: 'numeric-key map',
  other: 'other',
};

const CyclerButton: React.FC<Props> = ({ featureId, valueType, currentChartId, prefs, extraMenuItems }) => {
  const catalog = VALUE_TYPE_CATALOG[valueType];
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  // Right-aligned popovers extend leftward and can overflow the viewport
  // when the kebab sits near the screen's left edge on a narrow card.
  // Shift the popover right just enough to keep a small gutter from the
  // viewport edge, after the browser has laid it out at its natural pos.
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!menuOpen || !el) return;
    el.style.transform = '';
    const rect = el.getBoundingClientRect();
    const minLeft = 8;
    if (rect.left < minLeft) {
      el.style.transform = `translateX(${Math.ceil(minLeft - rect.left)}px)`;
    }
  }, [menuOpen]);

  if (!catalog || catalog.cycle.length < 2) return null;

  // Data-drift guard: if currentChartId isn't in this value-type's cycle
  // (e.g. user pinned 'line' on a feature whose value type later changed),
  // treat current index as -1 so the next click cycles to cycle[0].
  const idx = catalog.cycle.indexOf(currentChartId);
  const nextChartId = catalog.cycle[(idx + 1) % catalog.cycle.length];

  const valueTypeDefault = prefs.getValueTypePref(valueType) ?? systemDefaultFor(valueType);
  const featurePref = prefs.getFeaturePref(featureId);
  const isAlreadyValueTypeDefault = valueTypeDefault === currentChartId;

  return (
    <div className="flex items-center" ref={menuRef}>
      <button
        onClick={() => prefs.setFeatureChart(featureId, nextChartId)}
        title={`Show ${CHART_LABELS[nextChartId]}`}
        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
      >
        {CHART_ICONS[nextChartId]}
      </button>

      {(featurePref || (extraMenuItems && extraMenuItems.length > 0)) && (
        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            title="More chart options"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" />
            </svg>
          </button>
          {menuOpen && (
            <div
              ref={popoverRef}
              className="absolute right-0 z-10 mt-1 w-64 max-w-[calc(100vw-1rem)] rounded-md bg-white shadow-lg border border-gray-200 py-1 text-sm"
            >
              {featurePref && (
                <>
                  <button
                    disabled={isAlreadyValueTypeDefault}
                    onClick={async () => {
                      await prefs.setValueTypeChart(valueType, currentChartId);
                      await prefs.clearFeatureChart(featureId);
                      setMenuOpen(false);
                    }}
                    className={
                      'w-full text-left px-3 py-2 ' +
                      (isAlreadyValueTypeDefault
                        ? 'text-gray-400 cursor-default'
                        : 'text-gray-800 hover:bg-gray-100')
                    }
                  >
                    Set as default for all {VALUE_TYPE_LABELS[valueType]} features
                  </button>
                  <button
                    onClick={async () => {
                      await prefs.clearFeatureChart(featureId);
                      setMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-gray-800 hover:bg-gray-100"
                  >
                    Reset to default for this feature
                  </button>
                </>
              )}
              {featurePref && extraMenuItems && extraMenuItems.length > 0 && (
                <div className="my-1 border-t border-gray-200" />
              )}
              {extraMenuItems?.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    item.onClick();
                    setMenuOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-100"
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CyclerButton;
